import { useState, useEffect, useMemo } from 'react';
import { useSession } from './useSession.ts';
import { getUserSubscriptionStatus } from '../services/apiService.ts';

export interface UserProfile {
  user: {
    id: string;
    email?: string;
    created_at?: string;
    email_confirmed_at?: string;
  } | null;
  loading: boolean;
  error: string | null;
  isPro: boolean;
  hasOneTimeScans: boolean;
  subscription: {
    id?: string;
    plan?: string;
    active?: boolean;
    scan_limit?: number;
    scans_used?: number;
    current_period_end?: string;
    stripe_subscription_id?: string;
  } | null;
  entitlements: Array<{
    id: string;
    scans_remaining: number;
    granted_at: string;
  }>;
  usage: {
    totalScansUsed: number;
    totalScansAvailable: number | 'unlimited';
    nextResetDate?: string;
    hasActiveSubscription: boolean;
  };
}

export function useUserProfile(): UserProfile {
  const { session, loading: sessionLoading } = useSession();
  const [subscriptionData, setSubscriptionData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    console.log('[useUserProfile] Fetching user profile data...');
    
    async function fetchProfileData() {
      if (!session?.user) {
        console.log('[useUserProfile] No session, skipping profile fetch');
        setSubscriptionData(null);
        setLoading(false);
        return;
      }

      try {
        console.log('[useUserProfile] Getting subscription status for user:', session.user.id);
        const status = await getUserSubscriptionStatus();
        console.log('[useUserProfile] Subscription status received:', status);
        setSubscriptionData(status);
        setError(null);
      } catch (err) {
        console.error('[useUserProfile] Failed to fetch subscription data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load profile data');
        setSubscriptionData(null);
      } finally {
        setLoading(false);
      }
    }

    if (!sessionLoading) {
      fetchProfileData();
    }
  }, [session?.user?.id, sessionLoading]);

  // Memoize the profile data to avoid unnecessary re-renders
  const profile = useMemo((): UserProfile => {
    console.log('[useUserProfile] Computing profile data...', {
      hasSession: !!session,
      hasUser: !!session?.user,
      hasSubscriptionData: !!subscriptionData,
      loading: sessionLoading || loading,
    });

    if (sessionLoading || loading) {
      return {
        user: null,
        loading: true,
        error: null,
        isPro: false,
        hasOneTimeScans: false,
        subscription: null,
        entitlements: [],
        usage: {
          totalScansUsed: 0,
          totalScansAvailable: 0,
          hasActiveSubscription: false,
        },
      };
    }

    if (!session?.user) {
      return {
        user: null,
        loading: false,
        error: null,
        isPro: false,
        hasOneTimeScans: false,
        subscription: null,
        entitlements: [],
        usage: {
          totalScansUsed: 0,
          totalScansAvailable: 0,
          hasActiveSubscription: false,
        },
      };
    }

    // Extract data from the new subscription-status API response
    const isPro = subscriptionData?.isProUser || false;
    const hasOneTimeScans = subscriptionData?.hasOneTimeScans || false;
    const subscription = subscriptionData?.subscription;
    const creditsRemaining = subscriptionData?.credits_remaining || 0;

    console.log('[useUserProfile] Processing subscription data:', {
      isPro,
      hasOneTimeScans,
      creditsRemaining,
      subscription
    });

    // Calculate usage stats for the new unified system
    const totalScansUsed = subscription?.scans_used || 0;
    
    // For the new system:
    // - Pro users: scan_limit is null (unlimited) or a high number
    // - Free users: scan_limit is the total allowed, credits_remaining is what's left
    let totalScansAvailable: number | 'unlimited';
    
    if (isPro) {
      // Pro users have unlimited scans
      totalScansAvailable = 'unlimited';
    } else {
      // Free users: use credits_remaining as the available scans
      totalScansAvailable = creditsRemaining;
    }

    // Create a unified subscription object for the profile
    const profileSubscription = {
      id: subscription?.id,
      plan: isPro ? 'pro' : 'light',
      active: isPro || hasOneTimeScans,
      scan_limit: isPro ? null : (creditsRemaining + totalScansUsed), // Reconstruct original limit
      scans_used: totalScansUsed,
      current_period_end: subscription?.current_period_end,
      stripe_subscription_id: subscription?.stripe_subscription_id,
    };

    // Legacy entitlements format (for compatibility)
    const entitlements = hasOneTimeScans ? [{
      id: 'unified-entitlement',
      scans_remaining: creditsRemaining,
      granted_at: new Date().toISOString()
    }] : [];

    return {
      user: {
        id: session.user.id,
        email: session.user.email,
        created_at: session.user.created_at,
        email_confirmed_at: session.user.email_confirmed_at,
      },
      loading: false,
      error,
      isPro,
      hasOneTimeScans,
      subscription: profileSubscription,
      entitlements,
      usage: {
        totalScansUsed,
        totalScansAvailable,
        nextResetDate: subscription?.current_period_end,
        hasActiveSubscription: isPro,
      },
    };
  }, [session, subscriptionData, sessionLoading, loading, error]);

  console.log('[useUserProfile] Returning profile:', {
    hasUser: !!profile.user,
    isPro: profile.isPro,
    hasOneTimeScans: profile.hasOneTimeScans,
    loading: profile.loading,
    error: profile.error,
  });

  return profile;
} 